import React, { useState, useEffect } from 'react';
import {
  func, number, bool
} from 'prop-types';
import { connect, useSelector } from 'react-redux';
import styles from './variable.module.css';
import editorStyles from '../../editor.module.css';
import {
  relativeCategoryTypeValues,
  schemaPartItemTypeValues,
  variableTypeValues
} from '../../../../constants/types';
import { updateVariableAction, removeVariableAction } from '../../../../actions';
import { ItemBody, ItemHeader } from '../SurveyPartItem';
import {
  getTranslation,
  withLang,
  langType,
  getMessages
} from '../../../../i18n';
import RelativeVariableFeedback from '../RelativeVariableFeedback';
import AbsoluteVariableFeedback from '../AbsoluteVariableFeedback';
import { LocalizedInput, LocalizedRichTextArea } from '../../../LocalizedInput';

const defaultRelativeCategories = relativeCategoryTypeValues.map((type) => ({
  relativeCategory: {
    type
  },
  feedback: getMessages('defaultRelativeFeedback'),
  improvementSuggestions: {}
}));

const getDefaultAbsoluteCategories = (variable) => ([{
  absoluteCategory: {
    description: {},
    value: variable.questions.length
  },
  feedback: {},
  improvementSuggestions: {}
}]);

const getVariableType = (variable) => {
  if (variable && variable?.responseCategories[0].relativeCategory) {
    return variableTypeValues.RELATIVE;
  }
  return variableTypeValues.ABSOLUTE;
};

const getSelectedQuestionsLikertScale = (questions) =>
  (questions.length ? questions[0].responseOptions.length : 0);

// Test if question selection would be invalid. This includes cases like selected questions
// likert scale changes, selected question is removed or selected questions code
// is changed
const isSelectionDirty = (selectedQuestions, selectableQuestions, selectedScale) => {
  const selectedQuestionCodes = selectedQuestions.map((q) => q.code);
  const selectableQuestionCodes = selectableQuestions.map((q) => q.code);

  if (selectedScale === 0) {
    return false;
  }

  return selectableQuestions
    .filter((q) => selectedQuestionCodes.includes(q.code))
    .some((q) => q.responseOptions.length !== selectedScale)
      || selectedQuestions.filter((q) => !selectableQuestionCodes.includes(q.code)).length;
};

const questionIsSelected = (question, variable) => Boolean(variable.questions.find((q) => q.code === question.code));

const collectQuestionGroupQuestions = (surveySchemaPartItems) => surveySchemaPartItems
  .filter((item) => item.type === schemaPartItemTypeValues.QUESTION_GROUP)
  .reduce((acc, item) => acc.concat(item.questionGroup.questions.map((question) =>
    ({ ...question, responseOptions: item.questionGroup.responseOptions }))), []);

const Variable = ({
  lang,
  isOpen,
  toggleOpen,
  index,
  lockedFromEditing,
  dispatchUpdateVariable,
  dispatchRemoveVariable,
  translate: t
}) => {
  const { variables, surveySchemaPartItems } = useSelector((state) => state.surveySchemaPart.surveySchemaPartOptions);
  const [variableType, setVariableType] = useState(getVariableType(variables[index]));
  const [selectedQuestionsLikertScale, setSelectedQuestionsLikertScale] = useState(getSelectedQuestionsLikertScale(variables[index].questions));
  const questions = collectQuestionGroupQuestions(surveySchemaPartItems);

  useEffect(() => {
    if (isSelectionDirty(variables[index].questions, questions, selectedQuestionsLikertScale)) {
      dispatchUpdateVariable(index, { questions: [] });
      setSelectedQuestionsLikertScale(null);
    }
  }, [variables]);

  const onLocalizedInputChange = (language) => (event) => {
    dispatchUpdateVariable(index, {
      [event.target.name]: { [language]: event.target.value }
    });
  };

  const onLocalizedRTEChange = (language, name) => (value) => {
    dispatchUpdateVariable(index, {
      [name]: { [language]: value }
    });
  };

  const onQuestionSelectionChange = (event) => {
    const { checked } = event.target;
    const questionsList = checked
      ? variables[index].questions
        .concat(questions.find((question) => question.code === event.target.name))
      : variables[index].questions
        .filter((question) => question.code !== event.target.name);
    const changes = { questions: questionsList };

    // update selected scale only if removing last selected question or
    // selecting first one
    if (!questionsList.length || (questionsList.length === 1 && checked)) {
      setSelectedQuestionsLikertScale(getSelectedQuestionsLikertScale(questionsList));
    }

    // Handle absolute category values
    if (variableType === variableTypeValues.ABSOLUTE) {
      let minValue = questionsList.length;
      const maxValue = questionsList.length
        ? questionsList.length * questionsList[0].responseOptions.length : 0;

      const responseCategories = variables[index].responseCategories.map((rc, idx) => {
        if (idx === 0 || minValue === 0) {
          rc.absoluteCategory.value = minValue;
        } else if (rc.absoluteCategory.value > maxValue) {
          rc.absoluteCategory.value = maxValue - (variables[index].responseCategories.length - (idx + 1));
        } else if (rc.absoluteCategory.value <= minValue) {
          rc.absoluteCategory.value = minValue + 1;
          minValue += 1;
        }
        return rc;
      });
      Object.assign(changes, { responseCategories });
    }
    dispatchUpdateVariable(index, changes);
  };

  function onVariableTypeChange(event) {
    const newVariableType = event.target.value;
    setVariableType(newVariableType);
    const absoluteCategories = getDefaultAbsoluteCategories(variables[index]);
    const responseCategories = newVariableType === variableTypeValues.RELATIVE
      ? [...defaultRelativeCategories]
      : [...absoluteCategories];
    dispatchUpdateVariable(index, { responseCategories });
  }

  function onRemoveVariable() {
    dispatchRemoveVariable(index);
  }

  const getActions = () => (
    <div className={editorStyles.itemActions}>
      <div className={editorStyles.headerInput}>
        <select
          className={editorStyles.dimensionSelect}
          value={variableType}
          onChange={onVariableTypeChange}
          data-cy="survey-schema-part-variable-type-select"
          disabled={lockedFromEditing}
        >
          <option value={variableTypeValues.RELATIVE}>
            { t('surveySchemaParts.dimensions.type.relative') }
          </option>
          <option value={variableTypeValues.ABSOLUTE}>
            { t('surveySchemaParts.dimensions.type.absolute') }
          </option>
        </select>
      </div>
      <button
        type="button"
        className="actionButton icon--trash iconRemove"
        onClick={onRemoveVariable}
        title={t('common.remove')}
        disabled={lockedFromEditing}
      />
    </div>
  );

  const getDimensionName = () =>
    (
      <div className={editorStyles.contentColumn}>
        <h2 className={editorStyles.columnTitle}>
          {t('surveySchemaParts.dimensions.name')}
        </h2>
        <p>{t('surveySchemaParts.dimensions.nameDescription')}</p>
        <LocalizedInput
          name="name"
          values={variables[index].name}
          onInputChange={onLocalizedInputChange}
          placeholderTranslationNamespace="inLanguage"
          maxLength={255}
        />
      </div>
    );

  const getSelectableQuestion = (question) => {
    const questionScale = question.responseOptions.length;
    const questionText = getTranslation(question.text, lang);
    const labelText = `(${t('surveyParts.itemTitles.likert')} ${questionScale}) ${questionText}`;
    const checkboxId = `variable_${index}_question_${question.code}`;
    const isDisabled = selectedQuestionsLikertScale
        && questionScale !== selectedQuestionsLikertScale;

    return (
      <li key={question.code} title={questionText}>
        <input
          type="checkbox"
          id={checkboxId}
          name={question.code}
          disabled={lockedFromEditing || isDisabled}
          checked={questionIsSelected(question, variables[index])}
          onChange={onQuestionSelectionChange}
        />
        <label htmlFor={checkboxId}>{labelText}</label>
      </li>
    );
  };

  const getQuestionSelector = () =>
    (
      <div className={editorStyles.contentColumn}>
        <h2 className={editorStyles.columnTitle}>
          {t('surveyParts.questions')}
        </h2>
        <p>{t('surveySchemaParts.dimensions.questionsDescription')}</p>
        <ul className={styles.questionList}>
          {questions.map((question) => getSelectableQuestion(question))}
        </ul>
      </div>
    );

  const getDescription = () =>
    (
      <div className={editorStyles.contentColumn}>
        <h2 className={editorStyles.columnTitle}>
          {t('surveySchemaParts.dimensions.description')}
        </h2>
        <p>{t('surveySchemaParts.dimensions.descriptionDescription')}</p>
        <LocalizedRichTextArea
          name="description"
          values={variables[index].description}
          onInputChange={onLocalizedRTEChange}
          placeholderTranslationNamespace="inLanguage"
        />
      </div>
    );

  return (
    <>
      <ItemHeader
        isOpen={isOpen}
        titleTranslationKey="surveySchemaParts.dimensions.dimension"
        preview={getTranslation(variables[index].name, lang)}
        toggleOpen={toggleOpen}
      />
      <ItemBody
        isOpen={isOpen}
      >
        {getActions(index)}
        <div className={editorStyles.contentContainer}>
          {getDimensionName()}
          {getQuestionSelector()}
        </div>
        {getDescription()}
        {variableType === variableTypeValues.RELATIVE
          ? <RelativeVariableFeedback variableIndex={index} variable={variables[index]} />
          : (
            <AbsoluteVariableFeedback
              variableIndex={index}
              variable={variables[index]}
              lockedFromEditing={lockedFromEditing}
            />
          )}
      </ItemBody>
    </>
  );
};

Variable.propTypes = {
  index: number.isRequired,
  isOpen: bool.isRequired,
  toggleOpen: func.isRequired,
  dispatchUpdateVariable: func.isRequired,
  dispatchRemoveVariable: func.isRequired,
  lockedFromEditing: bool.isRequired,
  ...langType
};

const mapDispatchToProps = (dispatch) => ({
  dispatchUpdateVariable: (index, variable) =>
    dispatch(updateVariableAction(index, variable)),
  dispatchRemoveVariable: (index) =>
    dispatch(removeVariableAction(index))
});

export default connect(null, mapDispatchToProps)(withLang(Variable));
